/**
 * SignaturePad - Canvas-based signature capture component
 * 
 * Allows users to draw a signature with their finger/mouse.
 * Exports the signature as a PNG data URL.
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Eraser } from 'lucide-react';

const SignaturePad = ({
  onSignatureChange,
  initialSignature = null,
  width = 300,
  height = 150,
  className = ''
}) => {
  const canvasRef = useRef(null);
  const [hasSignature, setHasSignature] = useState(!!initialSignature);
  const [restoreState, setRestoreState] = useState(initialSignature ? 'loading' : 'ready');
  const modeRef = useRef(initialSignature ? 'loading' : 'ready');
  const mountedRef = useRef(false);
  const retireRestoreRef = useRef(() => {});
  const drawingRef = useRef({ active: false, changed: false });
  const lastPosRef = useRef({ x: 0, y: 0 });

  // Initialize canvas, then paint an existing signature on top if one was
  // handed in (e.g. restored from a draft). Mount-only by design — this
  // must not re-run when initialSignature changes later as a side effect
  // of the component's own onSignatureChange calls while drawing, or it
  // would clear and redraw the canvas after every stroke.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    mountedRef.current = true;
    let active = true;
    let img;
    const retire = () => {
      active = false;
      if (img) { img.onload = null; img.onerror = null; }
    };
    retireRestoreRef.current = retire;
    const current = () => active && mountedRef.current && canvasRef.current === canvas;
    const setMode = (mode) => { modeRef.current = mode; setRestoreState(mode); };
    const failed = () => {
      if (!current()) return;
      retire();
      setMode('failed');
    };

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (initialSignature) {
      setMode('loading');
      img = new Image();
      img.onload = () => {
        if (!current()) return;
        try { ctx.drawImage(img, 0, 0, canvas.width, canvas.height); }
        catch { failed(); return; }
        retire();
        setMode('ready');
      };
      img.onerror = failed;
      try { img.src = initialSignature; } catch { failed(); }
    }
    return () => {
      mountedRef.current = false;
      drawingRef.current = { active: false, changed: false };
      retire();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get position from event (handles both mouse and touch)
  const getPosition = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  }, []);

  const startDrawing = useCallback((e) => {
    e.preventDefault();
    if (!mountedRef.current || modeRef.current !== 'ready') return;
    const pos = getPosition(e);
    lastPosRef.current = pos;
    drawingRef.current = { active: true, changed: false };
  }, [getPosition]);

  const draw = useCallback((e) => {
    if (!mountedRef.current || modeRef.current !== 'ready' || !drawingRef.current.active) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    const pos = getPosition(e);
    
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    drawingRef.current.changed = true;

    lastPosRef.current = pos;
    
    if (!hasSignature) {
      setHasSignature(true);
    }
  }, [getPosition, hasSignature]);

  const stopDrawing = useCallback(() => {
    const stroke = drawingRef.current;
    drawingRef.current = { active: false, changed: false };
    const canvas = canvasRef.current;
    // Leaving/ending without a new stroke must not export a blank restoration.
    if (mountedRef.current && modeRef.current === 'ready' && canvas
        && stroke.active && stroke.changed && onSignatureChange) {
      onSignatureChange(canvas.toDataURL('image/png'));
    }
  }, [onSignatureChange]);

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!mountedRef.current || !ctx) return;

    retireRestoreRef.current();
    drawingRef.current = { active: false, changed: false };
    modeRef.current = 'ready';
    setRestoreState('ready');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    
    if (onSignatureChange) {
      onSignatureChange(null);
    }
  }, [onSignatureChange]);

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="relative">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="border-2 border-dashed border-stone-300 rounded-lg bg-white touch-none w-full"
          style={{ aspectRatio: `${width}/${height}` }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          onTouchCancel={stopDrawing}
          aria-busy={restoreState === 'loading'}
          aria-disabled={restoreState !== 'ready'}
          data-testid="signature-canvas"
        />
        {!hasSignature && restoreState === 'ready' && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-stone-400 text-sm">Sign here with finger</span>
          </div>
        )}
      </div>
      {restoreState !== 'ready' && (
        <p role="status" className="text-xs text-stone-500">
          {restoreState === 'loading' ? 'Loading saved signature… You can clear it to sign again.'
            : 'Could not display the saved signature. It has been kept. Clear to sign again, or choose Done to keep it.'}
        </p>
      )}
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clearSignature}
          className="text-xs"
          data-testid="clear-signature-btn"
        >
          <Eraser className="h-3 w-3 mr-1" />
          Clear
        </Button>
      </div>
    </div>
  );
};

export default SignaturePad;
